/**
 * How a connection actually got through — the number T1 exists to produce (`hosted.md §7.2`).
 *
 * A PAIR HAS TWO ENDS AND EITHER ONE BEING A RELAY MEANS THE TRAFFIC IS RELAYED: a link with a direct
 * local candidate and a remote TURN allocation still costs somebody the whole session's bandwidth. So
 * "the pair type" is not one fact — reporting one number would be `arena.md §C` with the connection as
 * its object. Both are read, and `relayed` is derived rather than stored beside them.
 */
/** The fields of `getStats()` this file reads, and no more — a shape, not a re-declaration of the spec. */
type StatsEntry = {
  type: string;
  selectedCandidatePairId?: string;
  localCandidateId?: string;
  remoteCandidateId?: string;
  candidateType?: string;
  protocol?: string;
  state?: string;
  nominated?: boolean;
  selected?: boolean;
};

export type IcePair = {
  localType: string;
  remoteType: string;
  /** The pair's protocol. A relay over TCP is the same allocation on a worse path. */
  localProtocol: string | null;
  relayed: boolean;
};

/**
 * Read the SELECTED pair, asking the transport first.
 *
 * Three ways to find it, and the order is not arbitrary: `transport.selectedCandidatePairId` is the one
 * the spec designates, `pair.selected` is what Firefox sets, and a nominated succeeded pair is the
 * fallback. Picking the first `succeeded` pair without nomination would report a candidate ICE
 * considered and did not choose — a plausible answer that is not the one asked for.
 */
export async function selectedPair(pc: RTCPeerConnection): Promise<IcePair | null> {
  /**
   * TYPED HERE AND NOT TAKEN FROM THE DOM LIB, which in this project's `lib` does not describe the
   * candidate stats at all and types the report without its `get`. At runtime `RTCStatsReport` IS a
   * read-only Map — asserting that is naming what the browser hands over, not widening a type to make
   * an error go away.
   */
  const stats = (await pc.getStats()) as unknown as ReadonlyMap<string, StatsEntry>;
  let pair: StatsEntry | null = null;

  stats.forEach((r) => {
    if (r.type !== 'transport' || !r.selectedCandidatePairId) return;
    const found = stats.get(r.selectedCandidatePairId);
    if (found) pair = found;
  });
  if (!pair) {
    stats.forEach((r) => {
      if (r.type !== 'candidate-pair') return;
      if (r.selected === true || (r.state === 'succeeded' && r.nominated === true)) pair = r;
    });
  }
  if (!pair) return null;

  const chosen: StatsEntry = pair;
  const local = chosen.localCandidateId ? stats.get(chosen.localCandidateId) : undefined;
  const remote = chosen.remoteCandidateId ? stats.get(chosen.remoteCandidateId) : undefined;
  if (!local || !remote) return null;

  const localType = String(local.candidateType ?? '');
  const remoteType = String(remote.candidateType ?? '');

  return {
    localType,
    remoteType,
    localProtocol: local.protocol ?? null,
    relayed: localType === 'relay' || remoteType === 'relay',
  };
}

/**
 * Remote candidates arriving before the remote description are HELD, not dropped.
 *
 * The precedent is `server/link/rtc.ts`, which pays for this with a measurement: an eight-Chrome run
 * logged six `addIceCandidate ... remote description was null` from this race, and ICE only recovered
 * because host candidates kept coming. Over a room channel the window is WIDER — an answer and a
 * candidate are two POSTs with no ordering between them. Bounded (`code-quality.md §10.1 P4`) and the
 * refusal COUNTED, so a link killed by discarded candidates differs from one still gathering.
 */
export class PendingIce {
  private held: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  dropped = 0;

  constructor(private readonly pc: RTCPeerConnection, private readonly max = 64) {}

  async add(c: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteSet) {
      if (this.held.length >= this.max) {
        this.dropped++;
        return;
      }
      this.held.push(c);
      return;
    }
    try {
      await this.pc.addIceCandidate(c);
    } catch {
      // A duplicate or late candidate is ordinary ICE traffic, not a failure.
    }
  }

  /** Called the moment `setRemoteDescription` resolves — the flush belongs to that event, not to a timer. */
  async remoteReady(): Promise<void> {
    this.remoteSet = true;
    const queued = this.held.splice(0);
    for (const c of queued) {
      try {
        await this.pc.addIceCandidate(c);
      } catch {
        // Same.
      }
    }
  }
}
